import React, { useEffect, useRef, useState } from "react";

/**
 * Wrapper that measures its own pixel size via ResizeObserver and exposes it
 * to the chart component as `chartWidth` / `chartHeight` props. This kills the
 * noisy Recharts "width(-1) / height(-1)" warning that fires when
 * ResponsiveContainer is asked to measure a CSS-grid child whose layout
 * hasn't settled by first paint.
 *
 * Usage:
 *   <ChartFrame className="h-[240px]">
 *     {(w, h) => (
 *       <BarChart width={w} height={h} data={...}>
 *         ...
 *       </BarChart>
 *     )}
 *   </ChartFrame>
 */
export default function ChartFrame({ className = "", style, children, "data-testid": testId }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(rect.height);
      if (w > 0 && h > 0) {
        setSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
      }
    };

    // Initial measurement after layout
    requestAnimationFrame(measure);

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className={`w-full min-w-0 ${className}`} style={style} data-testid={testId}>
      {size.w > 0 && size.h > 0 ? children(size.w, size.h) : null}
    </div>
  );
}
